import { tokenRefreshManager } from './token-refresh-manager';

/**
 * MessageHandler - Service for handling cross-origin postMessage communication
 * Responsible for receiving token from parent frame and triggering automatic login
 */
export class MessageHandler {
  private cleanup: (() => void) | null = null;
  private onTokenReceived: ((token: string) => void) | null = null;
  private parentOrigin: string | null = null;

  /**
   * Set callback to be triggered when token is received
   */
  setTokenReceivedCallback(callback: (token: string) => void): void {
    this.onTokenReceived = callback;
  }

  /**
   * Start listening for postMessage events from parent frame
   */
  startListening(): void {
    if (this.cleanup) {
      console.warn('MessageHandler already listening');
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      // Store parent origin for future communication
      if (event.origin && !this.parentOrigin) {
        this.parentOrigin = event.origin;
      }

      // Extract token from postMessage
      let token: string | null = null;

      if (event.data && event.data.token) {
        token = event.data.token;
      } else if (event.data && event.data.cortex_session) {
        token = event.data.cortex_session;
      }

      if (token) {
        // Store token in sessionStorage for immediate use
        this.storeTokenInSession(token);

        // Notify TokenRefreshManager that new token arrived (for 401 retry logic)
        tokenRefreshManager.notifyTokenReceived(token);

        // Trigger callback for automatic login
        if (this.onTokenReceived) {
          this.onTokenReceived(token);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Store cleanup function
    this.cleanup = () => {
      window.removeEventListener('message', handleMessage);
      this.cleanup = null;
    };
  }

  /**
   * Stop listening for postMessage events
   */
  stopListening(): void {
    if (this.cleanup) {
      this.cleanup();
    }
  }

  /**
   * Store token in sessionStorage
   */
  private storeTokenInSession(token: string): void {
    try {
      sessionStorage.setItem('cortex_session', token);
    } catch (error) {
      console.error('Failed to store token in sessionStorage:', error);
    }
  }

  /**
   * Request a fresh token from parent iframe
   * Sends postMessage to parent requesting token refresh
   */
  requestFreshToken(): void {
    if (!window.parent || window.parent === window) {
      return;
    }

    const targetOrigin = this.parentOrigin || '*';

    try {
      window.parent.postMessage(
        {
          type: 'REQUEST_TOKEN',
          timestamp: Date.now()
        },
        targetOrigin
      );
    } catch (error) {
      console.error('Failed to send token request to parent:', error);
    }
  }
}

/**
 * Singleton instance for easy access
 */
export const messageHandler = new MessageHandler();
